import React from "react";
import { Box, Portal, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { dayOfWeekOptions } from "./util/const";

const MINUTES_PER_DAY = 24 * 60;
const POPOVER_WIDTH = 430;
const TIMELINE_SIDE_PADDING = 28;

const timeToMinutes = (time) => {
  const [hour = 0, minute = 0] = String(time || "00:00").split(":").map(Number);

  return (hour * 60) + minute;
};

const formatTime = (time) => {
  if (time === "24:00") return "12:00AM";

  const minutes = timeToMinutes(time);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
};

const getSegments = (day) => {
  if (!day?.enabled) return [];

  const from = timeToMinutes(day.from);
  const to = timeToMinutes(day.to);

  if (from === to) {
    return [{ start: 0, end: MINUTES_PER_DAY }];
  }

  if (from < to) {
    return [{ start: from, end: to }];
  }

  return [
    { start: from, end: MINUTES_PER_DAY },
    { start: 0, end: to },
  ];
};

const SchedulePreviewRow = ({ day }) => {
  const theme = useTheme();
  const segments = getSegments(day);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "42px 1fr",
        alignItems: "center",
        gap: "14px",
        minHeight: "28px",
      }}
    >
      <Typography
        sx={{
          color: "common.white",
          fontSize: "13px",
          fontWeight: 700,
          lineHeight: "18px",
        }}
      >
        {day.shortLabel}
      </Typography>

      <Box sx={{ height: "20px", px: `${TIMELINE_SIDE_PADDING}px` }}>
        <Box sx={{ position: "relative", height: "20px" }}>
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              left: 0,
              right: 0,
              height: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.72)",
            }}
          />
          {segments.map((segment, index) => {
            const left = (segment.start / MINUTES_PER_DAY) * 100;
            const width = ((segment.end - segment.start) / MINUTES_PER_DAY) * 100;

            return (
              <Box
                key={`${segment.start}-${segment.end}-${index}`}
                sx={{
                  position: "absolute",
                  top: "8px",
                  left: `${left}%`,
                  width: `${width}%`,
                  height: "4px",
                  backgroundColor: theme.palette.primary.greenPrimary,
                }}
              />
            );
          })}
          {day.enabled && (
            <>
              <Typography
                sx={{
                  position: "absolute",
                  top: "-6px",
                  left: `${(timeToMinutes(day.from) / MINUTES_PER_DAY) * 100}%`,
                  transform: "translateX(-50%)",
                  color: "common.white",
                  fontSize: "10px",
                  lineHeight: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                {formatTime(day.from)}
              </Typography>
              <Typography
                sx={{
                  position: "absolute",
                  top: "13px",
                  left: `${(timeToMinutes(day.to) / MINUTES_PER_DAY) * 100}%`,
                  transform: "translateX(-100%)",
                  color: "common.white",
                  fontSize: "10px",
                  lineHeight: "12px",
                  whiteSpace: "nowrap",
                }}
              >
                {formatTime(day.to)}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const PreviewPopover = ({
  anchorPosition,
  profile,
  popoverRef,
  onPointerEnter,
  onPointerLeave,
}) => {
  const open = Boolean(anchorPosition && profile);
  const viewportWidth = window.innerWidth || POPOVER_WIDTH;
  const maxPopoverLeft = Math.max(viewportWidth - POPOVER_WIDTH - 16, 16);
  const popoverLeft = anchorPosition
    ? Math.min(
      Math.max(anchorPosition.left - (POPOVER_WIDTH / 2), 16),
      maxPopoverLeft,
    )
    : 0;
  const arrowLeft = anchorPosition ? anchorPosition.left - popoverLeft : POPOVER_WIDTH / 2;
  const dayMap = new Map((profile?.days || []).map((day) => [day.dayOfWeek, day]));
  const rows = dayOfWeekOptions.map((option) => ({
    ...option,
    ...(dayMap.get(option.key) || {}),
  }));

  if (!open) return null;

  return (
    <Portal>
      <Box
        ref={popoverRef}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.tooltip,
          top: `${anchorPosition.top + 8}px`,
          left: `${popoverLeft}px`,
          width: "430px",
          borderRadius: "6px",
          backgroundColor: "rgba(78, 78, 78, 0.94)",
          boxShadow: "0 3px 8px rgba(0, 0, 0, 0.35)",
          overflow: "visible",
          p: "14px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-7px",
            left: `${arrowLeft}px`,
            transform: "translateX(-50%) rotate(45deg)",
            width: "14px",
            height: "14px",
            backgroundColor: "rgba(78, 78, 78, 0.94)",
          },
        }}
      >
        {rows.map((day) => (
          <SchedulePreviewRow
            key={day.key}
            day={day}
          />
        ))}
      </Box>
    </Portal>
  );
};

export default PreviewPopover;
